import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom'
import Table from '../../../../components/table/Table';
import Modal from '../../../../components/template/Modal';
import EvaluationNewModal from './EvaluationNewModal';
import EvaluationModal from './EvaluationModal';
import Spinner from '../../../../components/template/Spinner';
import ButtonFull from '../../../../components/template/ButtonFull';
import api from '../../../../components/utils/api';
import { isFp32ConvertedModelReady, requiredFp32FormatLabel } from '../../../../components/utils/convertedModel';
import { testsetLabel } from '../../../../components/utils/testset';

const EvaluationPage = () => {
    const { generalModelId, mergeRuleId } = useParams();
    const [modalVisibleNew, setModalVisibleNew] = useState(false);
    const [modalVisibleResult, setModalVisibleResult] = useState(false);
    const [evaluations, setEvaluations] = useState([])
    const [resultData, setResultData] = useState({})
    const [isDisabled, setIsDisabled] = useState(false)
    const [isTorch, setIsTorch] = useState(undefined)
    const [isModelReady, setIsModelReady] = useState(null)
    const openModal = () => { setModalVisibleNew(true); }
    const closeModal = () => { setModalVisibleNew(false); }
    const openModalResult = () => { setModalVisibleResult(true); }
    const closeModalResult = () => { setModalVisibleResult(false); }

    useEffect(() => {
        const fetchEvaluations = async () => {
            try {
                const responses = await api.get(`/api/merge-rules/${mergeRuleId}/evaluations`)
                setEvaluations([...responses.data.evaluations])
            } catch (err) {
                console.error(err)
            }
        }
        if (mergeRuleId) {
            fetchEvaluations()
        }
    }, [mergeRuleId])

    useEffect(() => {
        const fetchGeneralModel = async () => {
            try {
                const res = await api.get(`/api/general-models/${generalModelId}`)
                setIsTorch(res.data.model_type === "Torch")
            } catch (err) {
                console.error(err)
            }
        }
        if (generalModelId) {
            fetchGeneralModel()
        }
    }, [generalModelId])

    useEffect(() => {
        const fetchIsModelReady = async () => {
            try {
                const res = await api.get(
                    `/api/merge-rules/${mergeRuleId}/converted-models/framework-map?is_torch=${isTorch}`
                )
                setIsModelReady(isFp32ConvertedModelReady(res.data.converted_model_map, isTorch))
            } catch (err) {
                console.error(err)
                setIsModelReady(false)
            }
        }
        if (mergeRuleId && isTorch !== undefined) {
            fetchIsModelReady()
        }
    }, [mergeRuleId, isTorch])

    const handleTriggerEvaluation = async (evaluation_id) => {
        setIsDisabled(true)
        try {
            await api.post(`/api/evaluations/${evaluation_id}/trigger`)
            setEvaluations((prevEvaluations) =>
                prevEvaluations.map((evaluation) =>
                    evaluation.evaluation_id === evaluation_id
                        ? { ...evaluation, status: 1 }
                        : evaluation
                )
            );
        } catch (error) {
            console.error(error)
        } finally {
            setIsDisabled(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                Header: "Testset",
                accessor: "testset",
                Cell: ({ row }) => <div>{testsetLabel(row.original.testset)}</div>
            },
            {
                Header: "Threshold",
                accessor: "threshold_id",
                Cell: ({ row }) => <div>{row.original.threshold.name}</div>
            },
            {
                Header: "Evaluation ID",
                accessor: "evaluation_id",
            },
            {
                Header: "Date",
                accessor: "created_at",
            },
            {
                Header: "Result",
                accessor: "result",
                Cell: ({ row }) => {
                    const isBlockedByModel = row.original.testset !== "internal" && isModelReady !== true;
                    return <>{
                        row.original.status === 2 ?
                            <div className='w-40'>
                                <ButtonFull contents={"Result"} onClick={() => { setResultData(row.original); openModalResult() }} />
                            </div>
                            :
                            row.original.status === 1 ?
                                <div className='w-40 flex font-semibold py-2 px-4 border rounded bg-transparent text-gray-500 border-gray-300'>
                                    <div className='pl-2'><Spinner /></div>
                                    <div className='m-auto cursor-default text-sm'>Evaluating...</div>
                                </div>
                                :
                                <div
                                    className='w-40'
                                    title={isBlockedByModel
                                        ? `Requires a completed ${requiredFp32FormatLabel(isTorch)} converted model. Convert it in the Convert tab first.`
                                        : undefined}
                                >
                                    <ButtonFull contents={"Evaluate"} onClick={() => { handleTriggerEvaluation(row.original.evaluation_id) }} isDisabled={isDisabled || isBlockedByModel} />
                                </div>
                    }</>
                }
            },
        ], [isDisabled, isModelReady, isTorch]
    )

    return (
        <div>
            <Table data={evaluations} columns={columns} additionalButton={{ contents: "New Evaluation", onClick: () => openModal() }} />
            {modalVisibleNew && <Modal
                visible={modalVisibleNew}
                closable={true}
                maskClosable={true}
                onClose={closeModal}>{
                    <EvaluationNewModal mergeRuleId={mergeRuleId} closeModal={closeModal} isTorch={isTorch} isModelReady={isModelReady} />
                }</Modal>}
            {modalVisibleResult && <Modal
                visible={modalVisibleResult}
                closable={true}
                maskClosable={true}
                onClose={closeModalResult}>{
                    <EvaluationModal resultData={resultData} mergeRuleId={mergeRuleId} />
                }</Modal>}
        </div>
    )
}

export default EvaluationPage